import type { ColumnDef, Row } from "@tanstack/react-table";
import { format, parse } from "date-fns";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { compareMatterSortParts } from "@/shared/utils/matterSort";
import { Badge } from "@/v2/components/ui/badge";
import { Button } from "@/v2/components/ui/button";
import { Checkbox } from "@/v2/components/ui/checkbox";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/v2/components/ui/tooltip";
import { AppIcon } from "@/v2/lib/appIcons";
import { cn } from "@/v2/lib/utils";
import { BillableIcon, getBillableMeta } from "./BillableIcon";
import { enumFilterFn } from "./filters/enumFilterFn";
import { textFilterFn } from "./filters/textFilterFn";
import type { Entry } from "./normalize";
import { getStatusMeta, StatusIcon } from "./StatusIcon";
import { useSelectionAPI } from "./selection";
import {
	formatTimekeeperName,
	getEntryTimekeeperLabel,
	getTimekeeperId,
	type Timekeeper,
} from "./timekeepers";

export type RowActions = {
	onEdit: (entry: unknown) => void;
	onDuplicate: (entries: unknown[]) => void;
	onExport: (entries: unknown[]) => void;
	onDelete: (entries: unknown[]) => void;
	onRestoreDeleted: (entries: unknown[]) => void;
	onMarkState: (entries: unknown[], target: string) => void;
};

/** Sidecar meta for callers that pass imperative row-level actions to TanStack. */
export type EntriesViewMeta = {
	rowActions?: RowActions;
};

const CURRENT_YEAR = new Date().getFullYear();

export function formatDate(iso: string): string {
	if (!iso) return "";
	const d = parse(iso, "yyyy-MM-dd", new Date());
	if (Number.isNaN(d.getTime())) return iso;
	return d.getFullYear() === CURRENT_YEAR
		? format(d, "MMM d")
		: format(d, "MMM d, yyyy");
}

/** Format the server-provided "HH:mm" wall-clock string to "h:mm AM/PM". */
function formatEndTime(t: string | null): string | null {
	if (!t) return null;
	const [hStr, mStr] = t.split(":");
	const h = Number(hStr);
	const m = Number(mStr);
	if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
	const period = h >= 12 ? "PM" : "AM";
	const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
	return `${hour12}:${String(m).padStart(2, "0")} ${period}`;
}

/** Hour 0-23 from "HH:mm", or -1 if absent/malformed. */
function hourOf(t: string | null): number {
	if (!t) return -1;
	const h = Number(t.slice(0, 2));
	return Number.isFinite(h) ? h : -1;
}

function SortableHeader({
	label,
	sorted,
	align = "left",
	hideLabel = false,
	onClick,
}: {
	label: string;
	sorted: false | "asc" | "desc";
	align?: "left" | "center" | "right";
	hideLabel?: boolean;
	onClick: () => void;
}) {
	const Icon =
		sorted === "asc" ? ArrowUp : sorted === "desc" ? ArrowDown : ArrowUpDown;
	return (
		<Button
			variant="ghost"
			size="sm"
			aria-label={hideLabel ? `Sort by ${label}` : undefined}
			onClick={onClick}
			className={cn(
				"h-8 gap-1 px-2 font-medium text-muted-foreground hover:text-foreground",
				hideLabel && "w-8 justify-center gap-0 px-0",
				align === "left" && "-ml-3",
				align === "center" && "mx-auto",
				align === "right" && "ml-auto -mr-3",
			)}
		>
			{!hideLabel && label}
			<Icon className={cn("size-3.5", !sorted && "opacity-40")} />
		</Button>
	);
}

function StaticHeader({
	label,
	align = "left",
	hideLabel = false,
}: {
	label: string;
	align?: "left" | "center" | "right";
	hideLabel?: boolean;
}) {
	return (
		<div
			title={hideLabel ? label : undefined}
			className={cn(
				"inline-flex h-8 items-center justify-center gap-1 whitespace-nowrap rounded-md px-2 text-sm font-medium text-muted-foreground",
				align === "left" && "-ml-3",
				align === "center" && "mx-auto",
				align === "right" && "ml-auto -mr-3",
			)}
		>
			{!hideLabel && label}
		</div>
	);
}

const BILLABLE_LABEL: Record<Entry["billableCategory"], string> = {
	billable: "Billable",
	non_billable: "Non-billable",
	billable_credit: "Credit",
	unknown: "Unknown",
};

const STATUS_LABEL: Record<Entry["status"], string> = {
	tracked: "Tracked",
	reviewed: "Reviewed",
	exported: "Exported",
	deleted: "Deleted",
};

// Lower bound for the Narrative column when resized, so its wrapped text stays
// readable.
export const NARRATIVE_MIN_PX = 120;

export const COLUMN_LABELS: Record<string, string> = {
	timekeeper: "Timekeeper",
	date: "Date",
	matter: "Matter",
	codes: "Codes",
	description: "Narrative",
	status: "Status",
	billableCategory: "Billing",
	units: "Units",
};

export const DEFAULT_COLUMN_ORDER = Object.keys(COLUMN_LABELS);

export const REQUIRED_COLUMNS = new Set<string>();

// ─────────────────────────────────────────────────────────
// Grouping
// ─────────────────────────────────────────────────────────
//
// Grouping values are display-ready labels, matching the v2 mobile entries
// list. The table renderer can therefore show `row.groupingValue` directly and
// read aggregate totals from TanStack instead of rebuilding labels/totals while
// rendering.

function hourLabel(h: number): string {
	if (h === 0) return "12AM";
	if (h === 12) return "12PM";
	return h < 12 ? `${h}AM` : `${h - 12}PM`;
}

/** "Thursday, May 5th" — drops the year when it matches CURRENT_YEAR. */
function formatLongDate(iso: string): string {
	if (!iso) return "";
	const d = parse(iso, "yyyy-MM-dd", new Date());
	if (Number.isNaN(d.getTime())) return iso;
	return d.getFullYear() === CURRENT_YEAR
		? format(d, "EEEE, MMMM do")
		: format(d, "EEEE, MMMM do, yyyy");
}

/** Human-readable label shown in the group separator row. */
export function getGroupLabel(
	e: Entry,
	columnId: string,
	timekeepers?: readonly Timekeeper[] | null,
): string {
	switch (columnId) {
		case "timekeeper":
			return getEntryTimekeeperLabel(e, timekeepers) ?? "No timekeeper";
		case "application":
			return e.application ?? "Unknown app";
		case "matter":
			return e.matter?.description ?? e.matter?.code ?? "—";
		case "codes": {
			const { task, activity } = e.codes ?? {};
			if (!task && !activity) return "No codes";
			return [task, activity].filter(Boolean).join(" · ");
		}
		case "units":
			return `${e.units.toFixed(1)} units`;
		case "status":
			return STATUS_LABEL[e.status];
		case "billableCategory":
			return BILLABLE_LABEL[e.billableCategory];
		default:
			return "";
	}
}

export function getDateHourLabel(e: Entry): string {
	const datePart = formatLongDate(e.date);
	const h = hourOf(e.endTime);
	if (h < 0) return `${datePart} · no time`;
	return `${datePart} · ${hourLabel(h)}`;
}

// Checkbox cell. Always behaves additively (ctrl-style); shift on the
// checkbox extends a range just like shift-clicking the row. Routing through
// the shared selection context keeps row-click and checkbox-click in sync.
function SelectCheckboxCell({ row }: { row: Row<Entry> }) {
	const { handleSelect } = useSelectionAPI();
	return (
		<button
			type="button"
			aria-label="Select row"
			onMouseDown={(e) => {
				if (e.shiftKey) e.preventDefault();
			}}
			onClick={(e) => {
				e.stopPropagation();
				handleSelect(row.id, e, { treatAsAdditive: true });
			}}
			className="-mx-3 -my-2 flex flex-1 cursor-pointer items-center justify-center px-3 py-2 hover:bg-muted/50"
		>
			<Checkbox
				checked={row.getIsSelected()}
				tabIndex={-1}
				className="pointer-events-none"
			/>
		</button>
	);
}

function timekeeperColumn(
	timekeepers: readonly Timekeeper[],
): ColumnDef<Entry> {
	return {
		id: "timekeeper",
		accessorFn: (e) => getTimekeeperId(e),
		getGroupingValue: (entry) => getTimekeeperId(entry) ?? "",
		aggregationFn: (columnId, leafRows) =>
			leafRows[0]?.getValue(columnId) ?? null,
		enableSorting: false,
		header: () => <StaticHeader label="Timekeeper" />,
		cell: ({ getValue, row }) => {
			const id = getValue<string | null>();
			const member = timekeepers.find((m) => m.id === id) ?? null;
			const label = member
				? formatTimekeeperName(member)
				: getEntryTimekeeperLabel(row.original, timekeepers);
			return (
				<span className="whitespace-nowrap text-foreground">
					{label ?? "–"}
				</span>
			);
		},
		filterFn: enumFilterFn,
		size: 132,
		minSize: 112,
	};
}

export const columns: ColumnDef<Entry>[] = [
	{
		id: "_select",
		enableGrouping: false,
		// `absolute inset-0` makes the button cover the entire cell so
		// `justify-center` resolves to the real column midpoint. The
		// previous `-mx-3 px-3 w-full` pattern collapsed the button's content
		// box to the cell's inner-padded area (12px), which shifted the
		// checkbox a few pixels left of the column center.
		header: ({ table }) => (
			<button
				type="button"
				aria-label="Select all"
				onClick={() =>
					table.toggleAllPageRowsSelected(!table.getIsAllPageRowsSelected())
				}
				className="absolute inset-0 flex cursor-pointer items-center justify-center"
			>
				<Checkbox
					checked={
						table.getIsAllPageRowsSelected()
							? true
							: table.getIsSomePageRowsSelected()
								? "indeterminate"
								: false
					}
					tabIndex={-1}
					className="pointer-events-none"
				/>
			</button>
		),
		// Cell wrapper is flex-col (see EntriesTable). `flex-1` makes this button
		// take the whole vertical track; the negative margins extend it past the
		// wrapper's padding so the click target is literally the entire cell —
		// no dead area around the checkbox.
		cell: ({ row }) => <SelectCheckboxCell row={row} />,
		enableSorting: false,
		enableHiding: false,
		enableResizing: false,
		size: 44,
	},
	{
		id: "application",
		accessorKey: "application",
		getGroupingValue: (entry) => getGroupLabel(entry, "application"),
		aggregationFn: (columnId, leafRows) =>
			leafRows[0]?.getValue(columnId) ?? null,
		header: ({ column }) => (
			<SortableHeader
				label="App"
				hideLabel
				align="center"
				sorted={column.getIsSorted()}
				onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
			/>
		),
		cell: ({ getValue }) => {
			const v = getValue<string | null>();
			return (
				<AppIcon
					application={v}
					className="mx-auto size-7 shrink-0 text-muted-foreground"
				/>
			);
		},
		filterFn: enumFilterFn,
		size: 64,
	},
	{
		id: "date",
		accessorKey: "date",
		getGroupingValue: getDateHourLabel,
		aggregationFn: (columnId, leafRows) =>
			leafRows[0]?.getValue(columnId) ?? null,
		size: 120,
		header: ({ column }) => (
			<SortableHeader
				label="Date"
				sorted={column.getIsSorted()}
				onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
			/>
		),
		cell: ({ row }) => {
			const time = formatEndTime(row.original.endTime);
			return (
				<div className="flex flex-col gap-0.5 whitespace-nowrap">
					<span className="tabular-nums text-foreground">
						{formatDate(row.original.date)}
					</span>
					{time && (
						<span className="text-xs tabular-nums text-muted-foreground">
							{time}
						</span>
					)}
				</div>
			);
		},
		// Sort by `date` + `endTime`'s wall-clock time-of-day as a single datetime.
		// Falls back to midnight when endTime is null. `endTime` is "HH:mm" — we
		// pad to "HH:mm:ss" so the string comparison stays lexicographic.
		sortingFn: (a, b) => {
			const toKey = (e: Entry): number => {
				const t = e.endTime ? `${e.endTime}:00` : "00:00:00";
				return Date.parse(`${e.date}T${t}Z`);
			};
			return toKey(a.original) - toKey(b.original);
		},
		// Range-aware string comparison — `date` is "YYYY-MM-DD" which sorts lex-correctly.
		filterFn: (row, _id, value: { from?: string; to?: string }) => {
			const d = row.original.date;
			if (value.from && d < value.from) return false;
			if (value.to && d > value.to) return false;
			return true;
		},
	},
	{
		id: "matter",
		size: 280,
		header: ({ column }) => (
			<SortableHeader
				label="Matter"
				sorted={column.getIsSorted()}
				onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
			/>
		),
		// Filter value is the matter code — that's what the chip menu lists.
		accessorFn: (e) => e.matter?.code ?? "",
		getGroupingValue: (entry) => getGroupLabel(entry, "matter"),
		aggregationFn: (columnId, leafRows) =>
			leafRows[0]?.getValue(columnId) ?? "",
		sortingFn: (a, b) =>
			compareMatterSortParts(
				{
					matterName: a.original.matter?.description,
					client: a.original.matter?.client,
					matterNumber: a.original.matter?.code,
				},
				{
					matterName: b.original.matter?.description,
					client: b.original.matter?.client,
					matterNumber: b.original.matter?.code,
				},
			),
		filterFn: enumFilterFn,
		cell: ({ row }) => {
			const matter = row.original.matter;
			const code = matter?.code ?? null;
			const description = matter?.description ?? null;
			const client = matter?.client ?? null;
			const primary = description ?? code ?? "–";
			// Show the code as a sublabel only when there's a description above it
			// to serve as the primary line.
			const showCode = !!code && !!description;
			return (
				<div className="flex flex-col leading-tight">
					{showCode && (
						<div className="text-xs text-muted-foreground">{code}</div>
					)}
					<div className="text-foreground">{primary}</div>
					{client && (
						<div className="text-xs text-muted-foreground">{client}</div>
					)}
				</div>
			);
		},
	},
	{
		id: "codes",
		size: 120,
		accessorFn: (e) =>
			`${e.codes?.task ?? ""} ${e.codes?.activity ?? ""}`.trim(),
		getGroupingValue: (entry) => getGroupLabel(entry, "codes"),
		aggregationFn: (columnId, leafRows) =>
			leafRows[0]?.getValue(columnId) ?? "",
		header: ({ column }) => (
			<SortableHeader
				label="Codes"
				align="center"
				sorted={column.getIsSorted()}
				onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
			/>
		),
		cell: ({ row }) => {
			const { task, activity } = row.original.codes ?? {};
			if (!task && !activity)
				return <div className="text-center text-muted-foreground">–</div>;
			return (
				<div className="flex flex-col items-center gap-0.5 text-center">
					{task && <span className="text-foreground">{task}</span>}
					{activity && (
						<span className="text-[0.9em] text-muted-foreground">
							{activity}
						</span>
					)}
				</div>
			);
		},
	},
	{
		id: "description",
		accessorKey: "description",
		enableGrouping: false,
		filterFn: textFilterFn,
		size: 360,
		minSize: NARRATIVE_MIN_PX,
		// Flex track (minmax(NARRATIVE_MIN_PX, 1fr) in EntriesTable) — its width is
		// derived from leftover space, not stored, so a resize handle would be a
		// no-op.
		enableResizing: false,
		// Not a SortableHeader (narrative isn't sortable), but matches their
		// typography + left offset so the header row reads consistently.
		header: () => (
			<span className="-ml-1 text-sm font-medium text-muted-foreground">
				Narrative
			</span>
		),
		cell: ({ getValue }) => (
			<p className="whitespace-pre-wrap text-foreground leading-snug">
				{getValue<string>()}
			</p>
		),
		enableSorting: false,
	},
	{
		id: "status",
		accessorKey: "status",
		getGroupingValue: (entry) => getGroupLabel(entry, "status"),
		aggregationFn: (columnId, leafRows) =>
			leafRows[0]?.getValue(columnId) ?? "tracked",
		size: 84,
		minSize: 84,
		enableResizing: false,
		header: ({ column }) => (
			<SortableHeader
				label="Status"
				align="center"
				sorted={column.getIsSorted()}
				onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
			/>
		),
		cell: ({ getValue }) => {
			const v = getValue<Entry["status"]>();
			const label = getStatusMeta(v).label;
			return (
				<div className="flex justify-center">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								aria-label={label}
								className="size-5 cursor-default hover:bg-transparent [&_svg]:size-5"
							>
								<StatusIcon status={v} />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="top">{label}</TooltipContent>
					</Tooltip>
				</div>
			);
		},
		filterFn: enumFilterFn,
	},
	{
		id: "billableCategory",
		accessorKey: "billableCategory",
		getGroupingValue: (entry) => getGroupLabel(entry, "billableCategory"),
		aggregationFn: (columnId, leafRows) =>
			leafRows[0]?.getValue(columnId) ?? "unknown",
		size: 104,
		minSize: 104,
		enableResizing: false,
		header: ({ column }) => (
			<SortableHeader
				label="Billing"
				align="center"
				sorted={column.getIsSorted()}
				onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
			/>
		),
		cell: ({ getValue }) => {
			const v = getValue<Entry["billableCategory"]>();
			const label = getBillableMeta(v).label;
			return (
				<div className="flex justify-center">
					<Tooltip>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								aria-label={label}
								className="size-5 cursor-default hover:bg-transparent [&_svg]:size-5"
							>
								<BillableIcon category={v} />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="top">{label}</TooltipContent>
					</Tooltip>
				</div>
			);
		},
		filterFn: enumFilterFn,
	},
	{
		id: "units",
		accessorKey: "units",
		getGroupingValue: (entry) => getGroupLabel(entry, "units"),
		aggregationFn: "sum",
		size: 80,
		minSize: 80,
		enableResizing: false,
		enableSorting: false,
		header: () => (
			<span className="flex w-full justify-center text-sm font-medium text-muted-foreground">
				Units
			</span>
		),
		cell: ({ getValue }) => (
			<div className="flex w-full justify-center">
				<Badge className="min-w-[50px] justify-center rounded-[7px] bg-foreground px-2.5 py-1.5 text-[0.8125rem] font-[525] leading-none tabular-nums text-background">
					{getValue<number>().toFixed(1)}
				</Badge>
			</div>
		),
	},
	// ─────────────────────────────────────────────────────────
	// Hidden filter-only columns. Not in COLUMN_LABELS so the Display Properties
	// chips can't reach them; locked invisible by enableHiding: false and
	// DEFAULT_COLUMN_VISIBILITY below.
	// ─────────────────────────────────────────────────────────
	{
		id: "client",
		accessorFn: (e) => e.matter?.client ?? "",
		enableGrouping: false,
		filterFn: enumFilterFn,
		enableHiding: false,
		enableResizing: false,
		enableSorting: false,
		header: () => null,
		cell: () => null,
	},
	{
		id: "task",
		accessorFn: (e) => e.codes?.task ?? "",
		enableGrouping: false,
		filterFn: enumFilterFn,
		enableHiding: false,
		enableResizing: false,
		enableSorting: false,
		header: () => null,
		cell: () => null,
	},
	{
		id: "activity",
		accessorFn: (e) => e.codes?.activity ?? "",
		enableGrouping: false,
		filterFn: enumFilterFn,
		enableHiding: false,
		enableResizing: false,
		enableSorting: false,
		header: () => null,
		cell: () => null,
	},
	// Infrastructure-only — no filterFn yet (numeric/text/boolean filters land in pass two).
	{
		id: "matterName",
		accessorFn: (e) => e.matter?.code ?? "",
		enableGrouping: false,
		enableHiding: false,
		enableResizing: false,
		enableSorting: false,
		header: () => null,
		cell: () => null,
	},
	{
		id: "billable",
		accessorKey: "billable",
		enableGrouping: false,
		enableHiding: false,
		enableResizing: false,
		enableSorting: false,
		header: () => null,
		cell: () => null,
	},
	{
		id: "unitsSeconds",
		accessorKey: "unitsSeconds",
		enableGrouping: false,
		enableHiding: false,
		enableResizing: false,
		enableSorting: false,
		header: () => null,
		cell: () => null,
	},
];

export function getEntriesColumns(
	timekeepers: readonly Timekeeper[] | null,
): ColumnDef<Entry>[] {
	if (timekeepers === null) return columns;
	const [selectColumn, ...rest] = columns;
	const appIndex = rest.findIndex((column) => column.id === "application");
	if (appIndex < 0)
		return [selectColumn, timekeeperColumn(timekeepers), ...rest];
	return [
		selectColumn,
		...rest.slice(0, appIndex + 1),
		timekeeperColumn(timekeepers),
		...rest.slice(appIndex + 1),
	];
}

export const DEFAULT_COLUMN_VISIBILITY: Record<string, boolean> = {
	application: false,
	client: false,
	task: false,
	activity: false,
	matterName: false,
	billable: false,
	unitsSeconds: false,
};
